import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Code2,
  Server,
  Layers3,
  Sparkles,
  X,
  Clock3,
  BriefcaseBusiness,
  Rocket,
} from "lucide-react";


// ============================================================
// ACCORDION
// ============================================================

const Accordion = ({ children }) => (
  <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
    {children}
  </div>
);


const AccordionItem = ({ title, content }) => {

  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="border-b border-gray-100 last:border-b-0">

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-5 px-5 py-5 text-left transition hover:bg-[#f8fafb]"
      >

        <div className="flex items-center gap-3">

          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#59b5cd]/10">
            <Code2
              size={17}
              className="text-[#076c93]"
            />
          </div>

          <span className="text-base font-bold text-[#07141a] sm:text-lg">
            {title}
          </span>

        </div>

        <motion.span
          animate={{
            rotate: isOpen ? 180 : 0,
          }}
          transition={{
            duration: 0.2,
          }}
          className="shrink-0 text-gray-400"
        >
          ↓
        </motion.span>

      </button>


      <AnimatePresence initial={false}>

        {isOpen && (

          <motion.div
            initial={{
              opacity: 0,
              height: 0,
            }}
            animate={{
              opacity: 1,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              height: 0,
            }}
            transition={{
              duration: 0.25,
              ease: "easeInOut",
            }}
            className="overflow-hidden"
          >

            <div className="px-5 pb-5 pl-[4.25rem]">

              <ul className="space-y-2.5">

                {content.map((item, index) => (

                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm leading-relaxed text-gray-600"
                  >

                    <CheckCircle2
                      size={16}
                      className="mt-0.5 shrink-0 text-[#59b5cd]"
                    />

                    <span>
                      {item}
                    </span>

                  </li>

                ))}

              </ul>

            </div>

          </motion.div>

        )}

      </AnimatePresence>

    </section>
  );
};


// ============================================================
// COURSE DESCRIPTION
// ============================================================

const CourseDescription = () => {

  const [showPopup, setShowPopup] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);


  // ==========================================================
  // SHOW DISCOUNT POPUP
  // ==========================================================

  useEffect(() => {

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000);

    return () => clearTimeout(timer);

  }, []);


  // ==========================================================
  // PAYMENT LINKS
  // ==========================================================

  const paymentLinks = {

    frontend:
      "https://paystack.com/buy/frontend-development-courses",

    backend:
      "https://paystack.com/buy/backend-development-courses",

    fullstack:
      "https://paystack.com/buy/software-development-courses-ucgtjo",

  };


  // ==========================================================
  // COURSE OPTIONS
  // ==========================================================

  const courseOptions = [

    {
      id: "frontend",

      title: "Frontend Engineering",

      shortTitle: "Frontend",

      price: "₦750,000",

      icon: Code2,

      color: "from-cyan-500 to-blue-600",

      description:
        "Learn to build modern, responsive and high-performance web interfaces using industry-standard frontend technologies.",

      features: [

        "HTML, CSS & JavaScript",

        "React & modern frontend development",

        "Responsive web design",

        "REST API integration",

        "Git & GitHub",

        "Code Agents AI",

        "Real-world frontend projects",

      ],

      popular: false,

    },


    {
      id: "backend",

      title: "Backend Engineering",

      shortTitle: "Backend",

      price: "₦850,000",

      icon: Server,

      color: "from-indigo-500 to-purple-600",

      description:
        "Learn how to build secure, scalable backend systems, APIs, databases and server-side applications.",

      features: [

        "Node.js & backend development",

        "RESTful APIs",

        "SQL & database design",

        "Authentication & authorization",

        "Backend architecture",

        "Deployment & server management",

      ],

      popular: false,

    },


    {
      id: "fullstack",

      title: "Full-Stack Engineering",

      shortTitle: "Full-Stack",

      price: "₦1,300,000",

      icon: Layers3,

      color: "from-[#076c93] to-[#59b5cd]",

      description:
        "Master frontend and backend engineering and learn how to build complete applications from idea to production.",

      features: [

        "Frontend & backend development",

        "React.js, Vue.js & Node.js",

        "Python development & Django",

        "APIs & database architecture",

        "Authentication & security",

        "Cloud deployment",

        "Real-world capstone projects",

  

      ],

      popular: true,

    },

  ];


  // ==========================================================
  // CURRICULUM
  // ==========================================================

  const curriculum = [

    {
      title: "Module 1: Computer Science & Programming Fundamentals",

      content: [

        "Programming logic and problem solving",

        "Variables, data types and control structures",

        "Functions and reusable code",

        "Object-oriented programming fundamentals",

        "Algorithms and data structures",

      ],
    },


    {
      title: "Module 2: Frontend Engineering",

      content: [

        "HTML5 and semantic web development",

        "CSS3 and responsive design",

        "Modern JavaScript",

        "React.js and component architecture",

        "State management",

        "API integration",

        "Frontend performance optimization",

      ],
    },


    {
      title: "Module 3: Backend Engineering",

      content: [

        "Node.js fundamentals",

        "Express.js and backend architecture",

        "REST API development",

        "Authentication and authorization",

        "Middleware and application security",

        "Server-side application development",

      ],
    },


    {
      title: "Module 4: Databases",

      content: [

        "Relational database concepts",

        "SQL and database queries",

        "Database schema design",

        "MySQL and PostgreSQL",

        "Relationships and indexing",

        "Database optimization",

      ],
    },


    {
      title: "Module 5: APIs & Application Architecture",

      content: [

        "RESTful API architecture",

        "API authentication",

        "Request and response handling",

        "Application architecture",

        "Error handling",

        "API testing",

      ],
    },


    {
      title: "Module 6: Git & Collaborative Development",

      content: [

        "Git fundamentals",

        "GitHub workflows",

        "Branching and merging",

        "Pull requests",

        "Code collaboration",

        "Version control best practices",

      ],
    },


    {
      title: "Module 7: Security",

      content: [

        "Secure authentication",

        "Password hashing",

        "JWT authentication",

        "Input validation",

        "OWASP security fundamentals",

        "Application security best practices",

      ],
    },


    {
      title: "Module 8: Cloud & Deployment",

      content: [

        "Application deployment",

        "Linux server fundamentals",

        "Environment variables",

        "Cloud hosting fundamentals",

        "Domain and SSL configuration",

        "Production deployment",

      ],
    },


    {
      title: "Module 9: Python & Django",

      content: [

        "Python programming fundamentals",

        "Django architecture",

        "Django models and databases",

        "Django REST APIs",

        "Authentication",

        "Building production applications",

      ],
    },


    {
      title: "Module 10: Capstone & Career Preparation",

      content: [

        "Build a complete production-style application",

        "Portfolio development",

        "Code review",

        "Technical interview preparation",

        "CV and LinkedIn optimization",

        "Career mentorship",

      ],
    },

  ];


  // ==========================================================
  // OPEN DISCOUNT MODAL
  // ==========================================================

  const openDiscount = (course) => {

    setSelectedCourse(course);

    setShowDiscountModal(true);

    setShowPopup(false);

  };


  return (

    <div className="relative w-full overflow-hidden bg-gradient-to-b from-white to-[#f8fafb] px-4 py-8 text-[#07141a] sm:px-6 lg:px-8">


     



      {/* =====================================================
          COURSE OPTIONS
      ====================================================== */}

      <section className="w-full">

      

        <div className="grid w-full grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">

          {courseOptions.map((course) => {

            const Icon = course.icon;

            return (

              <motion.div
                key={course.id}
                whileHover={{
                  y: -5,
                }}
                transition={{
                  duration: 0.2,
                }}
                className={`relative flex w-full flex-col overflow-hidden rounded-2xl border bg-white p-6 shadow-sm transition-all hover:shadow-xl ${
                  course.popular
                    ? "border-[#ecb32b]/60 ring-1 ring-[#ecb32c]/20"
                    : "border-gray-100"
                }`}
              >

                {/* Popular Badge */}

                {course.popular && (

                  <div className="absolute right-4 top-4 rounded-full bg-[#ecb32c] px-3 py-1 text-[10px] font-black uppercase tracking-wider text-[#07141a]">

                    Most Popular

                  </div>

                )}


                {/* Icon */}

                <div
                  className={`mb-5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${course.color} text-white shadow-lg`}
                >

                  <Icon size={22} />

                </div>


                {/* Title */}

                <h3 className="text-xl font-black text-[#07141a]">
                  {course.title}
                </h3>


                {/* Description */}

                <p className="mt-2 min-h-[72px] text-sm leading-relaxed text-gray-500">
                  {course.description}
                </p>


                <div className="my-5 border-t border-gray-100" />


                {/* Price */}

                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Programme Fee
                </p>

                <div className="mt-1">

                  <span className="text-3xl font-black text-[#07141a]">
                    {course.price}
                  </span>

                </div>


                {/* Features */}

                <div className="mt-5 flex-1 space-y-2">

                  {course.features.slice(0, 5).map(
                    (feature, index) => (

                      <div
                        key={index}
                        className="flex items-start gap-2 text-sm text-gray-600"
                      >

                        <CheckCircle2
                          size={16}
                          className="mt-0.5 shrink-0 text-[#59b5cd]"
                        />

                        <span>
                          {feature}
                        </span>

                      </div>

                    )
                  )}

                </div>


                {/* CTA */}

                <button
                  type="button"
                  onClick={() => openDiscount(course)}
                  className={`mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-bold transition ${
                    course.popular
                      ? "bg-[#ecb32c] text-[#07141a] hover:bg-[#f3c34f]"
                      : "bg-[#07141a] text-white hover:bg-[#076c93]"
                  }`}
                >

                  Enrol in {course.shortTitle}

                  <ArrowRight size={17} />

                </button>

              </motion.div>

            );

          })}

        </div>

      </section>



      {/* =====================================================
          CURRICULUM
      ====================================================== */}

      <section className="mt-12 w-full">

        <div className="mb-6">
          <p className="mt-2 text-sm text-gray-500">
            A practical engineering curriculum designed around modern
            software development.
          </p>

        </div>


        <Accordion>

          {curriculum.map((item, index) => (

            <AccordionItem
              key={index}
              title={item.title}
              content={item.content}
            />

          ))}

        </Accordion>

      </section>



      {/* =====================================================
          DISCOUNT POPUP
      ====================================================== */}

      <AnimatePresence>

        {showPopup && !showDiscountModal && (

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 30,
              scale: 0.95,
            }}
            className="fixed bottom-5 right-5 z-40 w-[calc(100%-2.5rem)] max-w-sm overflow-hidden rounded-2xl bg-[#07141a] p-5 text-white shadow-2xl"
          >

            <button
              type="button"
              onClick={() => setShowPopup(false)}
              className="absolute right-3 top-3 text-white/40 transition hover:text-white"
            >
              <X size={18} />
            </button>


            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#ecb32c]">
              🎁
            </div>


            <h3 className="text-lg font-black">
              Get 20% Off Your Programme
            </h3>


            <p className="mt-1 text-sm leading-relaxed text-white/55">
              Secure your place and take advantage of our limited-time
              enrollment offer.
            </p>


            <button
              type="button"
              onClick={() => {
                openDiscount(courseOptions[2]);
              }}
              className="mt-4 w-full rounded-xl bg-[#ecb32c] px-4 py-3 text-sm font-bold text-[#07141a] transition hover:bg-[#f3c34f]"
            >
              Claim My Discount
            </button>

          </motion.div>

        )}

      </AnimatePresence>



      {/* =====================================================
          DISCOUNT MODAL
      ====================================================== */}

      <AnimatePresence>

        {showDiscountModal && selectedCourse && (

          <>

            {/* Overlay */}

            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              onClick={() => setShowDiscountModal(false)}
              className="fixed inset-0 z-50 bg-[#07141a]/70 backdrop-blur-sm"
            />


            {/* Modal */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
                y: 20,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
                y: 20,
              }}
              className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto p-4"
            >

              <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">


                {/* Modal Header */}

                <div className="bg-[#07141a] p-6 text-white">

                  <button
                    type="button"
                    onClick={() => setShowDiscountModal(false)}
                    className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white/60 transition hover:bg-white/20 hover:text-white"
                  >

                    <X size={18} />

                  </button>


                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ecb32c] text-[#07141a]">

                    <Sparkles size={22} />

                  </div>


                  <p className="text-xs font-bold uppercase tracking-wider text-[#59b5cd]">
                    Limited Enrollment Offer
                  </p>


                  <h3 className="mt-2 text-2xl font-black">
                    Get 20% Off
                  </h3>


                  <p className="mt-1 text-sm text-white/50">
                    {selectedCourse.title}
                  </p>

                </div>


                {/* Modal Body */}

                <div className="p-6">


                  {/* Course Summary */}

                  <div className="mb-5 rounded-2xl bg-[#f8fafb] p-4">

                    <div className="flex items-center justify-between gap-4">

                      <div>

                        <p className="text-xs font-semibold text-gray-400">
                          Programme
                        </p>

                        <p className="mt-1 font-bold text-[#07141a]">
                          {selectedCourse.shortTitle}
                        </p>

                      </div>


                      <div className="text-right">

                        <p className="text-xs text-gray-400">
                          Regular Fee
                        </p>

                        <p className="font-bold text-[#07141a]">
                          {selectedCourse.price}
                        </p>

                      </div>

                    </div>

                  </div>


                  {/* Form */}

                  <div className="space-y-3">

                    <input
                      type="text"
                      placeholder="Full Name"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition focus:border-[#59b5cd] focus:ring-2 focus:ring-[#59b5cd]/10"
                    />


                    <input
                      type="email"
                      placeholder="Email Address"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition focus:border-[#59b5cd] focus:ring-2 focus:ring-[#59b5cd]/10"
                    />


                    <input
                      type="tel"
                      placeholder="Phone Number"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition focus:border-[#59b5cd] focus:ring-2 focus:ring-[#59b5cd]/10"
                    />

                  </div>


                  {/* Payment */}

                  <button
                    type="button"
                    onClick={() => {

                      const paymentUrl =
                        paymentLinks[selectedCourse.id];

                      if (paymentUrl) {

                        window.open(
                          paymentUrl,
                          "_blank",
                          "noopener,noreferrer"
                        );

                      }

                    }}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#ecb32c] px-5 py-4 font-bold text-[#07141a] shadow-lg transition hover:bg-[#f3c34f] hover:shadow-xl"
                  >

                    Pay Now & Get 20% Off

                    <ArrowRight size={18} />

                  </button>


                  <p className="mt-3 text-center text-[11px] leading-relaxed text-gray-400">
                    By proceeding, you'll be redirected to our secure
                    payment page to complete your enrollment.
                  </p>

                </div>

              </div>

            </motion.div>

          </>

        )}

      </AnimatePresence>

    </div>
  );
};


export default CourseDescription;